import React, { PureComponent } from 'react';

import { Container, Grid } from '@mui/material';
import { bool, func, object, string } from 'prop-types';
import QueryString from 'qs';
import { connect } from 'react-redux';
import { Route, Switch, withRouter } from 'react-router-dom';

import FullScreenLogo from 'components/FullScreenLogo';

import Sentry, { isSentryInitiated } from 'config/sentry';

import GeneralError from 'containers/ErrorPages/GeneralError';
import { QUIZ_ROUTE } from 'containers/LoversQuiz/Steps';
import { QUIZ_ROUTE as QUIZ_ROUTE_ALT } from 'containers/LoversQuizAlt/Steps';
import NewExplorePage from 'containers/NewExplorePage';
import RootPage from 'containers/RootPage';
import { SAC_ROUTE } from 'containers/SexualAnxietyClass';
import {
  CancelConfirmation,
  PromoApplied,
} from 'containers/SubscriptionCancelInformation';

import analytics from 'helpers/analytics';
import cookieStorage from 'helpers/cookieStorage';
import { getFacebookPixelParamsFromSearch, storeFacebookPixelParams } from 'helpers/facebook-pixel';
import { getReferrerFromSearch, storeReferrer } from 'helpers/referrer';
import { mapRoutes } from 'helpers/routes';
import showHiddenElements from 'helpers/show-hidden-elements';
import { isActiveSubscription } from 'helpers/subscription';
import { getUtmParamsToStore } from 'helpers/utm';

import { INFLUENCER_PARAMS, UTM_PARAMS } from 'store/consts';
import { getUserProfile } from 'store/modules/profile';
import { fetchUserAlreadyListenedStories } from 'store/modules/userAlreadyListenedStories';

import ProtectedRoute from 'routes/ProtectedRoute';
import storyRoutes from 'routes/StoryDetailRoute';

const Account = React.lazy(() => import('containers/Account'));
const AddToHomeScreenInstructions = React.lazy(() =>
  import('components/PWA/AddToHomeScreenInstructions'),
);
const AgeVerification = React.lazy(() => import('containers/AgeVerification'));
const BespokeCancelInformation = React.lazy(() =>
  import('containers/BespokeCancelInformation'),
);
const BrowseAll = React.lazy(() =>
  import('components/NewExplorePage/BrowseAll'),
);
const CancelSubscription = React.lazy(() =>
  import('components/CancelSubscription'),
);
const CancellationStep = React.lazy(() =>
  import('components/CancelSubscription/CancellationOffer'),
);
const CancelSubscriptionSuccess = React.lazy(() =>
  import('components/CancelSubscription/CancelSubscriptionSuccess'),
);

const SubscriptionReactivationInformation = React.lazy(() =>
  import('containers/SubscriptionReactivationInformation'),
);

const CancelSubscriptionError = React.lazy(() =>
  import('components/CancelSubscription/CancelSubscriptionError'),
);
const EditPlaylist = React.lazy(() =>
  import('components/Playlists/EditPlaylist'),
);
const ForgotPassword = React.lazy(() => import('containers/ForgotPassword'));
const ForgotPasswordConfirmation = React.lazy(() =>
  import('components/ForgotPasswordConfirmation'),
);
const GuidedSessions = React.lazy(() =>
  import('components/NewExplorePage/GuidedSessions'),
);
const ThreeSixtyAudios = React.lazy(() =>
  import('components/NewExplorePage/ThreeSixtyAudios'),
);
const HardcoreModal = React.lazy(() => import('components/HardcoreModal'));
const Home = React.lazy(() => import('containers/Home'));
const JourneyDetails = React.lazy(() => import('containers/JourneyDetails'));
const Journeys = React.lazy(() => import('containers/Journeys'));
const KinksPage = React.lazy(() =>
  import('components/NewExplorePage/KinksPageList'),
);
const LGBTQIA = React.lazy(() => import('components/NewExplorePage/LGBTQIA'));
const Sounds = React.lazy(() => import('components/NewExplorePage/Sounds'));
const ManageSubscription = React.lazy(() =>
  import('components/ManageSubscription'),
);
const MasturbationTechnique = React.lazy(() =>
  import('containers/GuidedMasturbations/MasturbationTechnique'),
);
const MoodDetailsPage = React.lazy(() => import('components/MoodDetailsPage'));
const NestedRedirect = React.lazy(() => import('components/NestedRedirect'));
const NotFoundPage = React.lazy(() =>
  import('containers/ErrorPages/NotFoundPage'),
);
const Onboarding = React.lazy(() => import('containers/Onboarding'));
const OnboardingResultsPage = React.lazy(() =>
  import('containers/Onboarding/Results/OnboardingResultsPage'),
);
const OnboardingVanilla = React.lazy(() =>
  import('containers/OnboardingVanilla'),
);
const OnboardingAlt = React.lazy(() => import('containers/OnboardingAlt'));
const PinModalSuccess = React.lazy(() => import('components/PinModalSuccess'));
const Playlists = React.lazy(() => import('components/Playlists'));
const PlaylistDetails = React.lazy(() =>
  import('components/Playlists/PlaylistDetails'),
);
const PostPurchaseOrchestrator = React.lazy(() =>
  import('components/PostPurchase/PostPurchaseOrchestrator'),
);
const PostPurchaseResults = React.lazy(() =>
  import('containers/Onboarding/PostPurchaseResults'),
);
const Recommendations = React.lazy(() => import('containers/Recommendations'));
const Scenarios = React.lazy(() =>
  import('components/NewExplorePage/Scenarios'),
);
const Search = React.lazy(() => import('containers/Search'));
const SearchResults = React.lazy(() => import('containers/SearchResults'));
const Selflove = React.lazy(() => import('components/NewExplorePage/Selflove'));
const SetNewPassword = React.lazy(() => import('containers/SetNewPassword'));
const SetPin = React.lazy(() => import('components/SetPin'));
const SignIn = React.lazy(() => import('containers/SignInHandler'));
const SignOut = React.lazy(() => import('containers/SignOut'));
const SignUp = React.lazy(() => import('containers/SignUpHandler'));
const SignUpConfirmation = React.lazy(() =>
  import('containers/SignUpConfirmation'),
);
const SpeakerStoriesPage = React.lazy(() =>
  import('containers/SpeakerStoriesPage'),
);
const LoversQuiz = React.lazy(() => import('containers/LoversQuiz'));
const LoversQuizAlt = React.lazy(() => import('containers/LoversQuizAlt'));
const StoryMarketsForm = React.lazy(() =>
  import('components/StoryMarketsForm'),
);
const StudentVerification = React.lazy(() =>
  import('components/StudentVerification'),
);
const SubscriptionPage = React.lazy(() =>
  import('components/SubscriptionPage'),
);
const TagDetailsPage = React.lazy(() => import('containers/TagDetailsPage'));
const UpdatePin = React.lazy(() => import('components/UpdatePin'));
const VerificationModal = React.lazy(() =>
  import('components/VerificationModal'),
);
const VerifyAge = React.lazy(() => import('containers/VerifyAge'));
const VerifyPin = React.lazy(() => import('components/VerifyPin'));
const VoicePage = React.lazy(() =>
  import('components/NewExplorePage/Voices/VoicePage'),
);
const Voices = React.lazy(() => import('components/NewExplorePage/Voices'));
const SexualAnxietyClass = React.lazy(() =>
  import('containers/SexualAnxietyClass'),
);

class App extends PureComponent {
  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  state = {
    hasError: false,
    isLoaded: false,
  };

  componentDidMount() {
    const {
      getUserProfile,
      accessToken,
      fetchUserAlreadyListenedStories,
      user,
    } = this.props;

    if (accessToken && !user) {
      getUserProfile()
        .then((data) => {
          this.setLoaded();

          analytics.identify(data.id, data.trackingId);

          this.checkUserForLoggedIn(data);
        })
        .catch(this.setLoaded);
      fetchUserAlreadyListenedStories();
    } else {
      this.setLoaded();
    }

    this.checkAndStoreUtmParameters();
    this.checkAndStoreReferrer();
    this.checkAndStoreFacebookPixelParameters();
    this.checkAndStoreInfluencerParameters();
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.IS_PRODUCTION !== 'true') {
      // eslint-disable-next-line no-console
      console.log(
        'Error captured in the main error boundary: ',
        error,
        errorInfo,
      );
    }

    if (isSentryInitiated) {
      Sentry.withScope((scope) => {
        Object.keys(errorInfo).forEach((key) => {
          scope.setExtra(key, errorInfo[key]);
        });
        Sentry.captureException(error);
      });
    }
  }

  checkAndStoreUtmParameters = () => {
    const utmParams = getUtmParamsToStore(this.props.location.search);

    if (utmParams) {
      cookieStorage.setItem(UTM_PARAMS, JSON.stringify(utmParams), {
        expires: 2,
      });
    }
  };

  checkAndStoreReferrer = () => {
    const referrer = getReferrerFromSearch(this.props.location.search);

    if (referrer) {
      storeReferrer(referrer);
    }
  };

  checkAndStoreFacebookPixelParameters = () => {
    const fbParams = getFacebookPixelParamsFromSearch(this.props.location.search);

    if (fbParams) {
      storeFacebookPixelParams(fbParams);
    }
  };

  checkAndStoreInfluencerParameters = () => {
    const { influencer } = QueryString.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });

    if (influencer) {
      cookieStorage.setItem(INFLUENCER_PARAMS, influencer);
    }
  };

  checkUserForLoggedIn = (user) => {
    if (user) {
      if (isActiveSubscription(user.subscriptionExpiresAt)) {
        return <NestedRedirect to="/browse" />;
      } else {
        return <NestedRedirect to="/explore" />;
      }
    }
  };

  setLoaded = () => {
    this.setState({ isLoaded: true });
    showHiddenElements();
  };

  render() {
    if (this.state.hasError) {
      return <GeneralError />;
    }

    const {
      match: { path },
    } = this.props;

    if (!this.state.isLoaded) {
      return <FullScreenLogo />;
    }
    const { location } = this.props;
    const { pathname } = location;

    return (
      <Container
        maxWidth="sm"
        disableGutters={true}
        sx={{ minHeight: 'var(--app-height)' }}
      >
        <Grid
          container
          flexDirection="column"
          justifyContent="center"
          sx={
            // don't set min height for onboarding pages
            !pathname.includes('onboarding') && {
              minHeight: 'var(--app-height)',
            }
          }
        >
          <Switch>
            {mapRoutes(storyRoutes)}

            <Route
              exact
              path={[
                `${path}sign-up`,
                `${path}ext/sign-up`,
                `${path}alt/sign-up`,
                `${path}alt/lovers-onboarding/sign-up`,
              ]}
              component={SignUp}
            />
            <Route
              path={[`${path}sign-in`, `${path}ext/sign-in`]}
              component={SignIn}
            />

            <Route
              exact
              path={[`${path}forgot-password`, `${path}ext/forgot-password`]}
              component={ForgotPassword}
            />
            <Route
              path={`${path}forgot-password/confirmation`}
              component={ForgotPasswordConfirmation}
            />
            <Route exact path={path} component={RootPage} />
            <Route
              path={`${path}confirmation/:token`}
              component={SignUpConfirmation}
            />
            <Route
              path={`${path}new-password/:token`}
              component={SetNewPassword}
            />
            <Route
              path={`${path}:section(browse)/recommendations`}
              component={Recommendations}
            />

            <ProtectedRoute
              exact
              path={`${path}explore/hardcore/learn-more`}
              component={VerificationModal}
            />

            <Route
              path={[
                `${path}:section?/speakers/:speakerId/stories`,
                `${path}:section(.*)/speakers/:speakerId/stories`,
              ]}
              component={SpeakerStoriesPage}
            />
            <Route
              path={[`${path}:section(.*)/tag/:tagId`]}
              component={TagDetailsPage}
            />
            <Route
              path={[`${path}:section(.*)/mood/:moodId`]}
              component={MoodDetailsPage}
            />

            <Route
              path={[`${path}:section(stories|explore)/all`]}
              component={BrowseAll}
            />

            <Route path={[`${path}:section(.*)/kinks`]} component={KinksPage} />
            <Route
              path={`${path}:section/guided-sessions/:id`}
              component={MasturbationTechnique}
            />
            <Route
              path={`${path}:section/guided-sessions`}
              component={GuidedSessions}
            />
            <Route
              path={`${path}:section/360-audios`}
              component={ThreeSixtyAudios}
            />
            <Route path={`${path}:section/self-love`} component={Selflove} />
            <Route path={`${path}:section(.*)/lgbtqia`} component={LGBTQIA} />
            <Route
              path={`${path}:section(.*/voices)/:voice`}
              component={VoicePage}
            />
            <Route path={`${path}:section(.*)/voices`} component={Voices} />
            <Route path={`${path}:section(.*)/sounds`} component={Sounds} />
            <Route path={`${path}:section/scenarios`} component={Scenarios} />

            <Route path={`${path}explore`} component={NewExplorePage} />
            <Route
              path={[
                `${path}:section(explore)/search/results`,
                `${path}:section(search/results)`,
              ]}
              component={SearchResults}
            />
            <Route
              path={[`${path}:section(explore)/search`, `${path}search`]}
              component={Search}
            />

            <Route
              path={`${path}subscription/cancel-confirmation`}
              component={CancelConfirmation}
            />
            <Route
              path={`${path}subscription/promo-applied`}
              component={PromoApplied}
            />

            <Route
              path={[
                `${path}**/subscription`,
                `${path}subscription`,
                `${path}pricing`,
              ]}
              component={SubscriptionPage}
            />

            <Route
              path={`${path}studentverification`}
              component={StudentVerification}
            />

            <Route path={`${path}:section(browse)`} component={Home} />
            <Route
              exact
              path={`${path}my-femtasy/account`}
              component={Account}
            />

            <ProtectedRoute
              path={`${path}my-femtasy/account/manage-subscription`}
              component={ManageSubscription}
            />

            <ProtectedRoute
              path={`${path}my-femtasy/account/cancel-bespoke`}
              component={BespokeCancelInformation}
            />

            <ProtectedRoute
              exact
              path={`${path}my-femtasy/account/cancel-subscription`}
              component={CancelSubscription}
            />

            <ProtectedRoute
              exact
              path={`${path}my-femtasy/account/cancel-subscription/step/:step`}
              component={CancellationStep}
            />

            <ProtectedRoute
              exact
              path={`${path}my-femtasy/account/cancel-subscription/success/:slug`}
              component={CancelSubscriptionSuccess}
            />

            <ProtectedRoute
              path={`${path}my-femtasy/account/subscription-reactivation-success`}
              component={SubscriptionReactivationInformation}
            />

            <ProtectedRoute
              exact
              path={`${path}my-femtasy/account/cancel-subscription/error`}
              component={CancelSubscriptionError}
            />

            <Route path={`${path}${QUIZ_ROUTE}`} component={LoversQuiz} />
            <Route
              path={`${path}${QUIZ_ROUTE_ALT}`}
              component={LoversQuizAlt}
            />
            <Route
              path={[
                `${path}onboarding/results`,
                `${path}alt/onboarding/results`,
              ]}
              component={OnboardingResultsPage}
            />
            <Route path={`${path}onboarding`} component={Onboarding} />
            <Route
              path={`${path}ext/onboarding`}
              component={OnboardingVanilla}
            />
            <Route path={`${path}alt/onboarding`} component={OnboardingAlt} />

            <Route
              path={`${path}preferences`}
              component={PostPurchaseResults}
            />

            <Route
              path={`${path}playlists/:id/edit`}
              component={EditPlaylist}
            />
            <Route path={`${path}playlists/:id`} component={PlaylistDetails} />
            <Route path={`${path}playlists`} component={Playlists} />

            <Route exact path={`${path}journeys`} component={Journeys} />
            <Route
              path={`${path}journeys/:journeyId`}
              component={JourneyDetails}
            />

            <Route
              path={`${path}add-to-home-screen`}
              component={AddToHomeScreenInstructions}
            />

            <ProtectedRoute
              path={`${path}my-femtasy/account/audio-languages`}
              component={StoryMarketsForm}
            />
            <ProtectedRoute
              exact
              path={`${path}my-femtasy/account/age-verification-status`}
              component={AgeVerification}
            />
            <ProtectedRoute
              exact
              path={`${path}my-femtasy/account/age-verification`}
              component={VerifyAge}
            />
            <ProtectedRoute
              exact
              path={`${path}my-femtasy/account/age-verification/pin/create`}
              component={SetPin}
            />
            <ProtectedRoute
              exact
              path={`${path}my-femtasy/account/age-verification/pin/update`}
              component={UpdatePin}
            />
            <ProtectedRoute
              exact
              path={`${path}my-femtasy/account/age-verification/pin/update/success`}
              component={PinModalSuccess}
            />

            <ProtectedRoute
              path={`${path}my-femtasy/account**`}
              component={Account}
            />
            <ProtectedRoute path={`${path}sign-out`} component={SignOut} />
            <ProtectedRoute
              exact
              path={`${path}(payment/success)`}
              component={PostPurchaseOrchestrator}
            />

            <ProtectedRoute
              exact
              path={`${path}${SAC_ROUTE}`}
              component={SexualAnxietyClass}
            />

            <Route path={`${path}404`} component={NotFoundPage} />
            {/* Keep this redirection on the end of Switch. */}
            <NestedRedirect to="/404" />
          </Switch>

          <Switch>
            <ProtectedRoute
              exact
              path={[`${path}:section(.*)/verify-pin`]}
              component={VerifyPin}
            />
            <ProtectedRoute
              exact
              path={[`${path}:section(.*)/hardcore`]}
              component={HardcoreModal}
            />
          </Switch>
        </Grid>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  accessToken: state.auth.accessToken,
  isAuthenticated: state.auth.isAuthenticated,
  user: state.profile.user,
  locale: state.translations.locale,
});

const mapDispatchToProps = {
  getUserProfile,
  fetchUserAlreadyListenedStories,
};

App.propTypes = {
  accessToken: string,
  getUserProfile: func,
  invokeReplacePlans: func,
  history: object,
  isAuthenticated: bool,
  fetchUserCountry: func,
  fetchUserAlreadyListenedStories: func,
  location: object,
  signIn: func,
  user: object,
  match: object,
  locale: string,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
